import { Injectable } from '@angular/core';
import { IRoom } from 'src/app/models/rooms';

interface CacheEntry<T> {
  data: T;
  savedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class RoomsCacheService {
  private readonly TTL_MS = 1 * 60 * 1000; // 1 minuto

  // --- Rooms list (by org) ---

  get(orgId: number | string): IRoom[] | null {
    return this.readEntry<IRoom[]>(`rooms_list_${orgId}`);
  }

  set(orgId: number | string, rooms: IRoom[]): void {
    this.writeEntry(`rooms_list_${orgId}`, rooms);
  }

  invalidate(orgId: number | string): void {
    localStorage.removeItem(`rooms_list_${orgId}`);
  }

  invalidateAllLists(): void {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('rooms_list_')) {
        localStorage.removeItem(key);
      }
    }
  }

  // --- Room detail (by room id) ---

  getRoom(roomId: number | string): any | null {
    return this.readEntry<any>(`room_detail_${roomId}`);
  }

  setRoom(roomId: number | string, room: any): void {
    this.writeEntry(`room_detail_${roomId}`, room);
  }

  invalidateRoom(roomId: number | string): void {
    localStorage.removeItem(`room_detail_${roomId}`);
    this.invalidateRoomStudentsPages(roomId);
  }

  // --- Paginated students payload (by room id + page + search) ---
  // Cada combinación de (page, pageSize, search) del endpoint
  // `/rooms/:id/students/` se guarda bajo
  // `room_students_${roomId}_p${page}_s${pageSize}_q${searchHash}` así un
  // refetch del mismo set usa el cache (TTL del servicio).

  getRoomStudentsPage(
    roomId: number | string,
    page: number,
    pageSize: number,
    search = '',
  ): any | null {
    return this.readEntry<any>(this.studentsPageKey(roomId, page, pageSize, search));
  }

  setRoomStudentsPage(
    roomId: number | string,
    page: number,
    pageSize: number,
    search: string,
    payload: any,
  ): void {
    this.writeEntry(
      this.studentsPageKey(roomId, page, pageSize, search),
      payload,
    );
  }

  /** Borra todas las páginas/queries cacheadas de students para un room
   *  (ej. tras eliminar un alumno: los índices, `count` y matches pueden
   *  cambiar). */
  invalidateRoomStudentsPages(roomId: number | string): void {
    const prefix = `room_students_${roomId}_`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  }

  private studentsPageKey(
    roomId: number | string,
    page: number,
    pageSize: number,
    search: string,
  ): string {
    // `search` lo normalizamos (trim + lowercase) para no fragmentar el
    // cache con espacios accidentales o capitalización distinta.
    const q = (search ?? '').trim().toLowerCase();
    return `room_students_${roomId}_p${page}_s${pageSize}_q${q}`;
  }

  // --- Generic helpers ---

  private readEntry<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const entry = JSON.parse(raw) as CacheEntry<T>;
      if (Date.now() - entry.savedAt > this.TTL_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return entry.data;
    } catch {
      return null;
    }
  }

  private writeEntry<T>(key: string, data: T): void {
    const entry: CacheEntry<T> = { data, savedAt: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  }
}
